import type { EnumUnion } from '@dresscode-frontend/utility-types';
import type { ApiResponseBody, ApiSearchQuery } from '@/lib/api/api-type-helper';

// API型定義
export type NetworkQuery = ApiSearchQuery<'/v2/networks', 'get'>;
export type NetworkListResponse = ApiResponseBody<'/v2/networks', 'get'>;
export type NetworkItem = NetworkListResponse['items'][number];
export type NetworkDetailResponse = ApiResponseBody<'/v2/networks/{id}', 'get'>;

// ネットワークソート順
export const NetworkOrderBy = {
  ID: 'ID',
  NETWORK_NAME: 'NETWORK_NAME',
  CONNECTION_TYPE: 'CONNECTION_TYPE',
  NETWORK_TYPE: 'NETWORK_TYPE',
  PURPOSE: 'PURPOSE'
} as const satisfies Record<NonNullable<NetworkQuery['orderBy']>, string>;
export type NetworkOrderBy = EnumUnion<typeof NetworkOrderBy>;

// ネットワークタイプ
export const NetworkTypeEnum = {
  INTERNAL: 'INTERNAL',
  EXTERNAL: 'EXTERNAL',
  VPN: 'VPN',
  DEDICATED_LINE: 'DEDICATED_LINE',
  CLOUD: 'CLOUD'
} as const satisfies Record<string, NonNullable<NetworkItem['networkType']>>;
export type NetworkType = EnumUnion<typeof NetworkTypeEnum>;

// 接続タイプ
export const ConnectionTypeEnum = {
  WIRED: 'WIRED',
  WIRELESS: 'WIRELESS',
  BOTH: 'BOTH'
} as const satisfies Record<string, NonNullable<NetworkItem['connectionType']>>;
export type ConnectionType = EnumUnion<typeof ConnectionTypeEnum>;

// アクセスレベル
export const AccessLevelEnum = {
  FULL_ACCESS: 'FULL_ACCESS',
  READ_ONLY: 'READ_ONLY',
  LIMITED: 'LIMITED',
  NO_ACCESS: 'NO_ACCESS'
} as const;

// アクセスステータス
export const AccessStatusEnum = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  SUSPENDED: 'SUSPENDED',
  EXPIRED: 'EXPIRED'
} as const;

// サブジェクトタイプ
export const SubjectTypeEnum = {
  USER: 'USER',
  DEVICE: 'DEVICE'
} as const;

// ネットワークステータス
export const NetworkStatusEnum = {
  CONNECTED: 'CONNECTED',
  OUT_OF_SERVICE: 'OUT_OF_SERVICE',
  ROAMING: 'ROAMING',
  DISCONNECTED: 'DISCONNECTED'
} as const;

// SIMステータス
export const SimStatusEnum = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  SUSPENDED: 'SUSPENDED',
  UNKNOWN: 'UNKNOWN'
} as const;

// 宛先タイプ（デバイス/場所）
export const DestinationTypeEnum = {
  DEVICE: 'DEVICE',
  PLACE: 'PLACE'
} as const satisfies Record<string, NonNullable<NetworkItem['destinationType']>>;

// ネットワークカテゴリ
export const NetworkCategory = {
  SHARED_INTERNAL: 'SHARED_INTERNAL',
  PERSONAL_USE: 'PERSONAL_USE',
  COMPANY_ASSET: 'COMPANY_ASSET',
  CUSTOMER_ENVIRONMENT: 'CUSTOMER_ENVIRONMENT',
  ALL: 'ALL'
} as const satisfies Record<string, NonNullable<NetworkItem['networkCategory']> | 'ALL'>;
export type NetworkCategory = EnumUnion<typeof NetworkCategory>;

// "ALL"を除外したネットワークカテゴリの型
export type NetworkCategoryWithoutAll = Exclude<NetworkCategory, 'ALL'>;

// 型ガード関数
const networkTypeKeys: Set<NetworkType> = new Set(Object.values(NetworkTypeEnum));
export function isValidNetworkType(value: string): value is NetworkType {
  return networkTypeKeys.has(value as NetworkType);
}